import { useState, useRef, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "@/services/i18n/client";
import { useAuth } from "@/lib/auth/use-auth";
import { getNavigationItems } from "@/lib/auth/roles";
import {
  ChevronDown,
  ScanLine,
  Send,
  MapPin,
  Package,
  Settings,
  FileText,
  Users,
  ShoppingBag,
  CreditCard,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

const iconMap: Record<string, LucideIcon> = {
  ScanLine,
  Send,
  MapPin,
  Package,
  Settings,
  FileText,
  Users,
  ShoppingBag,
  CreditCard,
  TrendingUp,
};

// Note: The i18next-cli extractor uses static analysis and cannot discover keys
// from dynamic calls. We use a static navLabels lookup map here so the
// extractor can see each key explicitly.
export function RoleNavigation() {
  const { user } = useAuth();
  const { t } = useTranslation("common");
  const location = useLocation();
  const navItems = getNavigationItems(user?.role?.slug);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navLabels: Record<string, string> = {
    "nav.home": t("nav.home"),
    "nav.scanning": t("nav.scanning"),
    "nav.packages": t("nav.packages"),
    "nav.receive": t("nav.receive"),
    "nav.ship": t("nav.ship"),
    "nav.inventory": t("nav.inventory"),
    "nav.inventoryAll": t("nav.inventoryAll"),
    "nav.settings": t("nav.settings"),
    "nav.integrations": t("nav.integrations"),
    "nav.destinations": t("nav.destinations"),
    "nav.scanLocations": t("nav.scanLocations"),
    "nav.externalDestinations": t("nav.externalDestinations"),
    "nav.contracts": t("nav.contracts"),
    "nav.analytics": t("nav.analytics"),
    "nav.adminDashboard": t("nav.adminDashboard"),
    "nav.companies": t("nav.companies"),
    "nav.adminContracts": t("nav.adminContracts"),
    "nav.users": t("nav.users"),
    "nav.billing": t("nav.billing"),
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  if (!user) return null;

  return (
    <nav className="flex items-center gap-1" ref={dropdownRef}>
      {navItems.map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        const isActive =
          location.pathname === item.href ||
          (hasChildren &&
            (item.children?.some(
              (child) =>
                location.pathname === child.href || location.pathname.startsWith(child.href + "/")
            ) ??
              false));

        if (hasChildren) {
          const isOpen = openDropdown === item.href;
          const IconComponent = item.icon ? iconMap[item.icon] : null;
          return (
            <div key={item.href} className="relative">
              <button
                onClick={() => setOpenDropdown(isOpen ? null : item.href)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
              >
                {IconComponent && <IconComponent className="h-4 w-4" />}
                {navLabels[item.label] ?? item.label}
                <ChevronDown
                  className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")}
                />
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 mt-1 z-50 min-w-[160px] rounded-md border bg-popover shadow-md py-1">
                  {item.children!.map((child) => {
                    const isChildActive =
                      location.pathname === child.href ||
                      location.pathname.startsWith(child.href + "/");
                    return (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={cn(
                          "block px-4 py-2 text-sm transition-colors",
                          isChildActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-foreground hover:bg-accent"
                        )}
                      >
                        {navLabels[child.label] ?? child.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        const IconComponent = item.icon ? iconMap[item.icon] : null;
        return (
          <NavLink
            key={item.href}
            to={item.href}
            end
            className={({ isActive: navIsActive }) =>
              cn(
                "flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                navIsActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent"
              )
            }
          >
            {IconComponent && <IconComponent className="h-4 w-4" />}
            {navLabels[item.label] ?? item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
